import { UnderlineButton } from 'src/components/Button/Button';
import { ReactComponent as ContractIcon } from 'src/assets/icons/contract.svg';
import { ReactComponent as InformationIcon } from 'src/assets/icons/information.svg';
import React, { useEffect } from 'react';
import useStakingPoolAddress from 'src/hooks/useStakingPoolAddress';
import { EnumValue } from '@multiversx/sdk-core/out';
import { stakingTypesEnum } from 'src/utils/smartContract';
import { displayedCurrencyCode, multiversxExplorerUrl, poolInformation, tokenDecimal, tokenIdentifier, environment } from 'src/config';
import useUserStakingHistory from 'src/hooks/useUserStakingHistory';
import { FormatAmount } from '@multiversx/sdk-dapp/UI';
import useTokenBalance from 'src/hooks/useTokenBalance';
import useApr from 'src/hooks/useApr';
import BigNumber from 'bignumber.js';

export default function StakingPool({
    discriminant,
    account,
    currentEpoch,
    updateTotalStakeAmount,
    tadaTokenBalance,
    minimumStaking,
}) {
    const stakingType = EnumValue.fromDiscriminant(stakingTypesEnum, discriminant);

    const { poolAddress, loading: poolAddressLoading } = useStakingPoolAddress(stakingType);
    const { userEntries } = useUserStakingHistory(account?.address, poolAddress);
    const { tokenBalance } = useTokenBalance(poolAddress, tokenIdentifier);
    const { apr } = useApr(stakingType);

    useEffect(() => {
        const totalStakeAmount = userEntries.reduce((sum, entry) => sum.plus(entry.stakeAmount), new BigNumber(0));
        updateTotalStakeAmount(discriminant, totalStakeAmount.toString());
    }, [userEntries, discriminant, updateTotalStakeAmount]);

    return (
        <div className="panel stakingPool">
            <div className="head">
                <div className="labeledValue">
                    <strong className="title">{poolInformation[discriminant].title}</strong>
                    <span className="subTitle">{poolInformation[discriminant].description}</span>
                </div>
                <div className="row">
                    <div className="labeledValue">
                        <i>APR</i>
                        <span>{apr}%</span>
                    </div>
                    <div className="separator"></div>
                    <div className="labeledValue">
                        <i>Total Staked</i>
                        <span>
                            <FormatAmount value={tokenBalance || 0} decimals={tokenDecimal} showLabel={false} />{' '}
                            <i>{displayedCurrencyCode}</i>
                        </span>
                    </div>
                </div>
            </div>
            <div className="subPanel stake">
                <div className="labeledValue">
                    <i>My stake</i>
                    <span>
                        <FormatAmount
                            value={userEntries
                                .reduce((sum, entry) => sum.plus(new BigNumber(entry.stakeAmount)), new BigNumber(0))
                                .toFixed()}
                            decimals={18}
                            showLabel={false}
                        />
                        <i> {displayedCurrencyCode}</i>
                    </span>
                </div>

                <div className="actions">
                    <UnderlineButton
                        disabled={poolAddressLoading}
                        href={multiversxExplorerUrl + '/accounts/' + poolAddress}
                        icon={<ContractIcon />}
                    >
                        Pool contrat
                    </UnderlineButton>
                </div>
            </div>
            {environment !== 'mainnet' && 
                <div className="information">
                    <InformationIcon />
                    <p>
                        Please note that the transaction history dates may appear incorrect on the Devnet. This discrepancy occurs because the epoch duration on the Devnet is 4 hours, whereas on the Mainnet, it is 24 hours. Rest assured, the transaction dates will be accurate on the Mainnet.
                    </p>
                </div>
            }
            <div className="subPanel history">
                <div className="grid-head">Date</div>
                <div className="grid-head">Delegate</div>
                <div className="grid-head">Expiration date</div>
                {userEntries.map((entry, index) => {
                    const expirationDays = entry.unbondEpoch - currentEpoch;
                    const expirationDate = new Date();

                    expirationDate.setUTCDate(expirationDate.getUTCDate() + expirationDays);

                    const transactionDate = new Date(expirationDate);
                    const poolInfo = poolInformation[discriminant];

                    if (poolInfo && poolInfo.unstakeDuration) {
                        transactionDate.setUTCDate(transactionDate.getUTCDate() - poolInfo.unstakeDuration);
                    }

                    return (
                        <React.Fragment key={index}>
                            <div>{transactionDate.toLocaleDateString()}</div>
                            <div>
                                <FormatAmount value={entry.stakeAmount.toFixed()} decimals={18} showLabel={false} />
                                <i> {displayedCurrencyCode}</i>
                            </div>
                            <div>{expirationDate.toLocaleDateString()}</div>
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}
